// src/services/companyMembersService.js
// Cliente frontend para el sistema de membresías técnico ↔ empresa.
//
// Backend: tabla public.company_members + RPCs en
// supabase/migrations/20260521000000_company_members.sql
//
// Todo cambio de estado pasa por RPCs (security definer) que validan auth.uid()
// y transiciones. Las lecturas usan RLS o RPCs específicas.
//
// Si la migración no fue aplicada todavía, las llamadas devuelven errores
// graceful y la UI los muestra como vacío (no rompe la app).
import supabase from '../supabase';
import emailService from './emailService';

const MIGRATION_MISSING_CODES = ['42P01', 'PGRST116', '42883'];
function isMigrationMissing(error) {
  if (!error) return false;
  if (MIGRATION_MISSING_CODES.includes(error.code)) return true;
  const msg = (error.message || '').toLowerCase();
  return msg.includes('does not exist') || msg.includes('relation "company_members"');
}

// ─── MUTATIONS ─────────────────────────────────────────────────────

// Técnico solicita unirse a una empresa
export async function requestJoinCompany(companyId, roleInCompany = null) {
  const { data, error } = await supabase.rpc('request_join_company', {
    p_company_id: companyId,
    p_role_in_company: roleInCompany,
  });
  if (error) {
    console.error('[companyMembers] requestJoinCompany:', error);
    return { ok: false, error: error.message };
  }
  // Notificar a la empresa (fire-and-forget)
  emailService.notifyMembershipEvent(data, 'request_received').catch(() => {});
  return { ok: true, membershipId: data };
}

// Empresa invita a un técnico.
// Fase 2: si el caller administra varias empresas, pasar companyId explícito.
// Fase 1 / legacy: si companyId es null, la RPC asume que el caller ES la empresa.
export async function inviteMember(userId, roleInCompany = null, companyId = null) {
  const { data, error } = await supabase.rpc('invite_member', {
    p_user_id: userId,
    p_role_in_company: roleInCompany,
    p_company_id: companyId,
  });
  if (error) {
    console.error('[companyMembers] inviteMember:', error);
    return { ok: false, error: error.message };
  }
  emailService.notifyMembershipEvent(data, 'invite_received').catch(() => {});
  return { ok: true, membershipId: data };
}

// Aceptar o rechazar una solicitud/invitación
export async function respondMembership(membershipId, accept) {
  const { error } = await supabase.rpc('respond_membership', {
    p_membership_id: membershipId,
    p_accept: accept,
  });
  if (error) {
    console.error('[companyMembers] respondMembership:', error);
    return { ok: false, error: error.message };
  }
  // Para distinguir si fue la empresa la que respondió (notificamos al user)
  // necesitamos saber el requested_by — pero como el caller es la empresa cuando
  // requested_by='user', el endpoint notify-membership ya valida quién es el caller
  // y decide a quién mandar. Disparamos los dos eventos posibles y el endpoint
  // ignora el que no aplique según el caller.
  emailService.notifyMembershipEvent(membershipId, accept ? 'request_approved' : 'request_rejected').catch(() => {});
  return { ok: true };
}

// Quitar miembro (lo puede hacer la empresa o el propio técnico)
export async function removeMembership(membershipId) {
  const { error } = await supabase.rpc('remove_membership', {
    p_membership_id: membershipId,
  });
  if (error) {
    console.error('[companyMembers] removeMembership:', error);
    return { ok: false, error: error.message };
  }
  // El endpoint solo manda email si la empresa removió (no si el user se fue)
  emailService.notifyMembershipEvent(membershipId, 'member_removed').catch(() => {});
  return { ok: true };
}

// ─── QUERIES (privadas, para los dueños de los datos) ──────────────

// Listar todos los miembros de MI empresa (uso del dashboard)
// Devuelve membresías + datos básicos del user
export async function getCompanyMembers(companyId, { status } = {}) {
  try {
    let query = supabase
      .from('company_members')
      .select('id, user_id, status, requested_by, role_in_company, role, requested_at, responded_at')
      .eq('company_id', companyId)
      .order('requested_at', { ascending: false });
    if (status) query = query.eq('status', status);

    const { data: members, error } = await query;
    if (error) {
      if (isMigrationMissing(error)) return { ok: true, members: [] };
      return { ok: false, error: error.message };
    }
    if (!members?.length) return { ok: true, members: [] };

    // Enriquecer con perfil del usuario
    const userIds = [...new Set(members.map(m => m.user_id))];
    const { data: profiles } = await supabase
      .from('user_profiles')
      .select('id, first_name, last_name, profile_image, job_title, email, is_public_profile, public_slug')
      .in('id', userIds);

    const profileById = Object.fromEntries((profiles || []).map(p => [p.id, p]));
    const enriched = members.map(m => ({
      ...m,
      profile: profileById[m.user_id] || null,
    }));
    return { ok: true, members: enriched };
  } catch (err) {
    if (isMigrationMissing(err)) return { ok: true, members: [] };
    return { ok: false, error: err.message };
  }
}

// Listar MIS membresías como técnico (todas, en cualquier estado)
export async function getMyMemberships(userId) {
  try {
    const { data: memberships, error } = await supabase
      .from('company_members')
      .select('id, company_id, status, requested_by, role_in_company, role, requested_at, responded_at')
      .eq('user_id', userId)
      .order('requested_at', { ascending: false });

    if (error) {
      if (isMigrationMissing(error)) return { ok: true, memberships: [] };
      return { ok: false, error: error.message };
    }
    if (!memberships?.length) return { ok: true, memberships: [] };

    const companyIds = [...new Set(memberships.map(m => m.company_id))];
    const { data: companies } = await supabase
      .from('company_profiles')
      .select('id, company_name, logo, banner, city, country')
      .in('id', companyIds);

    const companyById = Object.fromEntries((companies || []).map(c => [c.id, c]));
    const enriched = memberships.map(m => ({
      ...m,
      company: companyById[m.company_id] || null,
    }));
    return { ok: true, memberships: enriched };
  } catch (err) {
    if (isMigrationMissing(err)) return { ok: true, memberships: [] };
    return { ok: false, error: err.message };
  }
}

// ─── QUERIES PÚBLICAS (cualquiera puede leer estos) ────────────────

// Equipo público de una empresa (solo membresías active)
export async function getCompanyTeam(companyId) {
  try {
    const { data, error } = await supabase.rpc('get_company_team_public', {
      p_company_id: companyId,
    });
    if (error) {
      if (isMigrationMissing(error)) return { ok: true, team: [] };
      return { ok: false, error: error.message };
    }
    return { ok: true, team: data || [] };
  } catch (err) {
    if (isMigrationMissing(err)) return { ok: true, team: [] };
    return { ok: false, error: err.message };
  }
}

// Empresas a las que pertenece un técnico (solo active)
export async function getUserCompanies(userId) {
  try {
    const { data, error } = await supabase.rpc('get_user_companies_public', {
      p_user_id: userId,
    });
    if (error) {
      if (isMigrationMissing(error)) return { ok: true, companies: [] };
      return { ok: false, error: error.message };
    }
    return { ok: true, companies: data || [] };
  } catch (err) {
    if (isMigrationMissing(err)) return { ok: true, companies: [] };
    return { ok: false, error: err.message };
  }
}

const companyMembersService = {
  requestJoinCompany,
  inviteMember,
  respondMembership,
  removeMembership,
  getCompanyMembers,
  getMyMemberships,
  getCompanyTeam,
  getUserCompanies,
};

export default companyMembersService;
